import React, { useRef } from "react";
import NumberInput from "common/NumberInputWithSpinner";
import TextInput from "common/TextInput";
import FoldablePanel from "common/FoldablePanel";
import SwitchInput from "common/SwitchInputWithState";
import { getFormData, rankAndParseFields } from "./utils";

const RTTISettings = ({ defaults = {}, onInputChange, bqn }) => (
  <>
    <h2 className="card-inside-title">Internet Side RTT Parameters</h2>
    <p>
      TCPO Measurements are classified into 3 groups depending on the internet
      side RTT (round-trip time). The RTTi-small and RTTi-large parameters are
      the thresholds that divide the internet traffic into small, medium and
      large groups.k
    </p>
    <NumberInput
      name={`rtti.small[bqn ${bqn}]`}
      label="RTTi-small"
      defaultValue={defaults.small}
      backspaceValue="0.010"
      onChange={onInputChange}
      onBlur={onInputChange}
      className="input-control align-left"
      autocomplete="off"
      units="seconds"
      min={0}
      step={0.01}
      precision={2}
      hideArrows={false}
    />
    <NumberInput
      name={`rtti.large[bqn ${bqn}]`}
      label="RTTi-large"
      defaultValue={defaults.large}
      backspaceValue="0.060"
      onChange={onInputChange}
      onBlur={onInputChange}
      className="input-control align-left"
      autocomplete="off"
      units="seconds"
      min={0}
      step={0.01}
      precision={2}
      hideArrows={false}
    />
  </>
);

const AIWSettings = ({ defaults = {}, onInputChange, bqn, metadata }) => (
  <>
    <SwitchInput
      defaultChecked={defaults.enabled}
      className={"sw-input"}
      name={`adaptativeInitialWindow.enabled[bqn ${bqn}]`}
      label="Adaptative initial window"
      onChange={onInputChange}
      tooltipValues={metadata?.enabled}
    />
    <NumberInput
      name={`adaptativeInitialWindow.size[bqn ${bqn}]`}
      label="Initial window size"
      defaultValue={defaults.size}
      onChange={onInputChange}
      className="input-control align-left"
      autocomplete="off"
      units="packets"
      min={0}
      precision={0}
      hideArrows={false}
      backspaceValue={10}
    />
  </>
);

let tcpParametersOpen = false;

const AdvancedParameters = ({ defaults, onInputChange, bqn, metadata }) => (
  <FoldablePanel
    title="Advanced Parameters"
    opened={tcpParametersOpen}
    onChange={(value) => (tcpParametersOpen = value)}
  >
    <AIWSettings
      defaults={defaults.adaptativeInitialWindow}
      onInputChange={onInputChange}
      bqn={bqn}
      metadata={metadata?.adaptativeInitialWindow}
    />
    <RTTISettings defaults={defaults.rtti} onInputChange={onInputChange} bqn={bqn}/>
  </FoldablePanel>
);

const ACMSettings = ({ defaults, onInputChange, bqn, metadata }) => (
  <>
    <h2 className="card-inside-title">Automatic Congestion Management</h2>
    <p>
      If disabled, no policies will apply ACM. If enabled, only policies with
      ACM enabled will apply ACM.
    </p>
    <SwitchInput
      defaultChecked={defaults.enabled}
      className={"sw-input"}
      name={`acm.enabled[bqn ${bqn}]`}
      label="Global ACM Status"
      onChange={onInputChange}
      tooltipValues={metadata?.enabled}
    />
  </>
);

const ShapingSettings = ({ defaults, onInputChange, bqn, metadata }) => (
  <>
    <h2 className="card-inside-title">Shaping</h2>
    <p>
      If disabled, no shaping or rate limit will be applied, even if configured.
    </p>
    <SwitchInput
      defaultChecked={defaults.perFlow}
      className={"sw-input"}
      name={`shaping.perFlow[bqn ${bqn}]`}
      label="Individual flow shaping (per flow)"
      onChange={onInputChange}
      tooltipValues={metadata?.perFlow}
    />
    <SwitchInput
      defaultChecked={defaults.perSubscriber}
      className={"sw-input"}
      name={`shaping.perSubscriber[bqn ${bqn}]`}
      label="Aggregated flow shaping (per subscriber)"
      onChange={onInputChange}
      tooltipValues={metadata?.perSubscriber}
    />
    <SwitchInput
      defaultChecked={defaults.ratePerSubscriber}
      className={"sw-input"}
      name={`shaping.ratePerSubscriber[bqn ${bqn}]`}
      label="Subscriber rate limiting"
      onChange={onInputChange}
      tooltipValues={metadata?.ratePerSubscriber}
    />
    <SwitchInput
      defaultChecked={defaults.ratePerSubscriberGroup}
      className={"sw-input"}
      name={`shaping.ratePerSubscriberGroup[bqn ${bqn}]`}
      label="Subscriber-group rate limiting"
      onChange={onInputChange}
      tooltipValues={metadata?.ratePerSubscriberGroup}
    />
  </>
);

const RedirectionSettings = ({ defaults, onInputChange, bqn }) => {
  return (<div><h2 className="card-inside-title">Redirection for blocked subscribers</h2>
   <TextInput
      type="text"
      name={`redirection.blockRedirectionIPv4URL[bqn ${bqn}]`}
      label="IPv4/HTTP redirect URL:"
      defaultValue={defaults.blockRedirectionIPv4URL}
      onChange={onInputChange}
      className="input-control margin-b-0"
    />
     <TextInput
      type="text"
      name={`redirection.blockRedirectionIPv6URL[bqn ${bqn}]`}
      label="IPv6/HTTP redirect URL:"
      defaultValue={defaults.blockRedirectionIPv6URL}
      onChange={onInputChange}
      className="input-control margin-b-0"
    />
  </div>);

};

const SystemSettings = ({ onChange, defaults = {}, state = {}, bqn, metadata }) => {
  const form = useRef(null);
  const onInputChange = () => {
    const values = getFormData(form.current);
    const parsedSettings = rankAndParseFields(values, state);

    // Restore raw strings for redirection URLs to prevent the parser from turning IP addresses into floats
    if (parsedSettings.redirection) {
      const ipv4Key = `redirection.blockRedirectionIPv4URL[bqn ${bqn}]`;
      const ipv6Key = `redirection.blockRedirectionIPv6URL[bqn ${bqn}]`;
      
      if (ipv4Key in values) parsedSettings.redirection.blockRedirectionIPv4URL = values[ipv4Key];
      if (ipv6Key in values) parsedSettings.redirection.blockRedirectionIPv6URL = values[ipv6Key];
    }

    onChange(parsedSettings);
  };
  return (
    <form ref={form}>
      <h2 className="card-inside-title">TCP Parameters</h2>
      <p>Global TCP related parameters.</p>
      <SwitchInput
        defaultChecked={defaults.tcp}
        className={"sw-input"}
        name={`tcp[bqn ${bqn}]`}
        label="Overall TCP Optimization"
        onChange={onInputChange}
        tooltipValues={metadata?.tcp}
      />
      <AdvancedParameters
        defaults={defaults}
        state={state}
        onInputChange={onInputChange}
        bqn={bqn}
        metadata={metadata}
      />
      <ACMSettings
        defaults={defaults.acm}
        state={state}
        onInputChange={onInputChange}
        bqn={bqn}
        metadata={metadata?.acm}
      />
      <ShapingSettings
        defaults={defaults.shaping}
        state={state.shaping}
        onInputChange={onInputChange}
        bqn={bqn}
        metadata={metadata?.shaping}
      />
      <RedirectionSettings
        defaults={defaults.redirection}
        state={state.redirection}
        onInputChange={onInputChange}
        bqn={bqn}
      />
    </form>
  );
};

export default SystemSettings;
